/**
 * ConversationHeader Component
 * 
 * Header for the message view showing:
 * - Back button (mobile only)
 * - Conversation name (person or group name)
 * - Participant count for groups
 * - Online status indicator
 * - Info button for group details
 */

import React from 'react';
import { Users, Info, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CONVERSATION_TYPES } from '@/lib/firestore';
import { UserAvatar } from '@/components/shared';

export default function ConversationHeader({
  conversation,
  displayName,
  avatar,
  participantId,
  onInfoClick,
  onBack,
  showBackButton = false,
  infoOpen = false
}) {
  const isGroup = conversation?.type === CONVERSATION_TYPES.GROUP;
  const participantCount = conversation?.participants?.length || 0;

  return (
    <div data-ui="thread-head" className="flex items-center justify-between px-2 sm:px-4 py-3 border-b border-slate-200/50 bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-xl relative z-0">
      {/* Left side - Back button (mobile), Avatar and name */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        {/* Back button - visible on mobile only */}
        {showBackButton && (
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onBack}
            data-ui="ibtn"
            className="md:hidden text-slate-500 dark:text-slate-400 hover:text-blue-500 flex-shrink-0 -ml-1"
            data-testid="mobile-back-btn"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
        )}

        {/* Avatar - using unified UserAvatar */}
        {isGroup ? (
          <div data-ui="avatar" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
        ) : (
          <UserAvatar 
            userId={participantId}
            name={displayName}
            avatarUrl={avatar}
            size="md"
            className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0"
          />
        )}

        {/* Name and status */}
        <div className="min-w-0 flex-1">
          <h2 data-ui="thread-name" className="font-semibold text-slate-800 dark:text-slate-100 truncate text-sm sm:text-base">{displayName}</h2>
          <p data-ui="thread-sub" className="text-xs text-slate-500 dark:text-slate-400">
            {isGroup 
              ? `${participantCount} participants`
              : 'Direct message'
            }
          </p>
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Info button */}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onInfoClick}
          data-ui="ibtn"
          data-open={infoOpen ? 'true' : undefined}
          className="text-slate-500 dark:text-slate-400 hover:text-blue-500 w-9 h-9 sm:w-10 sm:h-10"
          data-testid="conversation-info-btn"
        >
          <Info className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
